import { useState, useCallback, useContext } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../configurations/global.config';
import { AuthContext } from '../contexts/user-context/user-context';
import { useSnackBar } from '../contexts/snackbar';
import { IDriverInvoice } from '../interfaces/common/driver-invoices';
import { IMerchantOrdersInvoice } from '../interfaces/common/merchant-invoices';

interface UseInvoicePDFViewerReturn {
  pdfUrl: string | null;
  isLoading: boolean;
  showPdfViewer: boolean;
  pdfTitle: string;
  pdfFilename: string;
  fetchAndViewInvoice: (
    invoice: IDriverInvoice | IMerchantOrdersInvoice,
    type: 'driver' | 'merchant'
  ) => Promise<void>;
  closePdfViewer: () => void;
}

/**
 * Custom hook for fetching and viewing invoice PDFs
 * Handles the logic for downloading PDFs from backend and displaying them in a viewer
 */
export function useInvoicePDFViewer(): UseInvoicePDFViewerReturn {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('Invoice Preview');
  const [pdfFilename, setPdfFilename] = useState('invoice.pdf');

  const fetchAndViewInvoice = useCallback(
    async (
      invoice: IDriverInvoice | IMerchantOrdersInvoice,
      type: 'driver' | 'merchant'
    ) => {
      try {
        setIsLoading(true);

        // Construct endpoint based on invoice type
        const endpoint =
          type === 'driver'
            ? `${API_ENDPOINT}/api/v1/driver-invoice/${invoice.id}/print/`
            : `${API_ENDPOINT}/api/v1/merchant-invoice/${invoice.id}/`;

        // Fetch PDF from backend
        const response = await axios.get(endpoint, {
          responseType: type === 'driver' ? 'arraybuffer' : 'blob',
          ...config,
        });

        // Create blob and URL
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        // Clean up previous URL if exists
        if (pdfUrl) {
          window.URL.revokeObjectURL(pdfUrl);
        }

        // Prepare filename
        const driverInvoice = invoice as IDriverInvoice;
        const formattedDate = new Date(invoice.created_at)
          .toLocaleDateString('en-GB')
          .replace(/\//g, '-');
        const name =
          type === 'driver'
            ? driverInvoice.driver?.name ?? 'driver'
            : 'merchant';
        const filename = `${invoice.id}.${name}.${formattedDate}.pdf`;

        // Set state and show viewer
        setPdfUrl(url);
        setPdfTitle(
          type === 'driver'
            ? `Driver Invoice #${invoice.id}`
            : `Merchant Invoice #${invoice.id}`
        );
        setPdfFilename(filename);
        setShowPdfViewer(true);
      } catch (error) {
        console.error('Failed to load invoice PDF:', error);
        showMessage('Failed to load invoice PDF', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [config, pdfUrl, showMessage]
  );

  const closePdfViewer = useCallback(() => {
    setShowPdfViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  }, [pdfUrl]);

  return {
    pdfUrl,
    isLoading,
    showPdfViewer,
    pdfTitle,
    pdfFilename,
    fetchAndViewInvoice,
    closePdfViewer,
  };
}

export default useInvoicePDFViewer;




